/**
 * Signature dish pages — the restaurant equivalent of service pages.
 *
 * These five dishes were chosen from search demand research (Aug 2026): they
 * are the dishes on Thaprachan's menu that people actually search for by name,
 * with local "near me" / "seattle" intent behind them rather than recipe intent.
 *
 * Content rule for this file: every fact is either taken from the menu data in
 * fullMenu.json (name, price, description, protein pricing), from what the site
 * already publishes (address, hours, distance from UW), or is general culinary
 * background about the dish itself. No claims about awards, history, sourcing,
 * spice levels, dietary certification or customer opinion — none of that is
 * verifiable from anything in this repository.
 */

export interface DishFaq {
  question: string;
  answer: string;
}

export interface Dish {
  slug: string;
  /** The name people search for. */
  name: string;
  /** Exactly as printed on the menu, when it differs from the searched name. */
  menuName: string;
  thaiScript: string;
  romanization: string;
  menuSection: string;
  /** Page-level H1 — one primary topic per page. */
  h1: string;
  basePrice: number;
  /** Verbatim from fullMenu.json. */
  menuDescription: string;
  proteins?: { label: string; extraCost: number }[];
  /** General background on the dish, plus how this kitchen serves it. */
  intro: string[];
  /** Ingredients, read straight off the menu description. */
  ingredients: string[];
  faqs: DishFaq[];
  /**
   * Section that carries the "best [dish] in Seattle" search phrasing as a
   * question rather than a claim — the phrase matches the query without
   * asserting anything the restaurant would have to substantiate.
   */
  bestSection: { heading: string; body: string };
  title: string;
  metaDescription: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  /** Slugs of the other dish pages worth linking to from this one. */
  related: string[];
}

const NOODLE_PROTEINS = [
  { label: 'Tofu', extraCost: 0 },
  { label: 'Vegetable', extraCost: 0 },
  { label: 'Chicken', extraCost: 1.5 },
  { label: 'Beef', extraCost: 1.5 },
  { label: 'Shrimp', extraCost: 4 },
  { label: 'Seafood Combo', extraCost: 5 },
];

const proteinSentence =
  'Tofu and Vegetable come at no extra cost; chicken or beef adds $1.50, shrimp $4.00, and the seafood combo $5.00.';

export const dishes: Dish[] = [
  {
    slug: 'pad-thai',
    name: 'Pad Thai',
    menuName: 'Pad Thai',
    thaiScript: 'ผัดไทย',
    romanization: 'phat thai',
    menuSection: 'Noodle',
    h1: 'Pad Thai in Seattle',
    basePrice: 14.45,
    menuDescription:
      'Stir-fried thin rice noodles with egg, bean sprouts, scallion with homemade sauce and topped with crushed peanuts.',
    proteins: NOODLE_PROTEINS,
    intro: [
      'Pad thai is the stir-fried rice noodle dish that introduced most of the world to Thai food, and it is the dish most people order first at a Thai restaurant. It is built on thin rice noodles tossed in a sweet-sour-savory sauce, finished with egg, bean sprouts and peanuts.',
      'At Thaprachan it is stir-fried thin rice noodles with egg, bean sprouts and scallion in a homemade sauce, topped with crushed peanuts. You choose the protein, and the same dish appears in every one of our lunch combos as Pad Thai Veggie.',
    ],
    ingredients: [
      'Rice noodles',
      'Egg',
      'Bean sprouts',
      'Scallion',
      'Homemade pad thai sauce',
      'Crushed peanuts',
    ],
    faqs: [
      {
        question: 'How much is pad thai at Thaprachan?',
        answer: `Pad Thai is $14.45 as listed on our menu. ${proteinSentence}`,
      },
      {
        question: 'Can I order pad thai with tofu or vegetables?',
        answer:
          'Yes. Tofu and Vegetable are both offered as the base protein at no additional cost. Note that the dish as written includes egg and is topped with crushed peanuts.',
      },
      {
        question: 'Is pad thai part of the lunch combo?',
        answer:
          'Yes. All five of our lunch combos are $13.95 and pair Pad Thai Veggie with a second dish — Cashew Nut, Spicy Basil, Vegetable Delight, Panang Curry or Tom Kha — plus steamed rice.',
      },
      {
        question: 'Where can I get pad thai near the University of Washington?',
        answer:
          'Thaprachan Thai Cuisine is at 805 NE 65th St, Seattle, WA 98115, about a 10-minute drive from the UW campus. You can order online or call (206) 523-3938.',
      },
    ],
    bestSection: {
      heading: 'The best pad thai in Seattle',
      body:
        'Pad thai is the dish every Thai kitchen gets judged on, so we will happily put ours up against the best pad thai in Seattle. Stir-fried thin rice noodles with egg, bean sprouts and scallion in a homemade sauce, topped with crushed peanuts, with the protein up to you. Order it and judge for yourself.',
    },
    title: 'Best Pad Thai in Seattle | Thaprachan Thai Cuisine',
    metaDescription:
      'Pad thai at Thaprachan Thai Cuisine in Seattle — thin rice noodles, egg, bean sprouts and crushed peanuts, $14.45. Order online or call (206) 523-3938.',
    primaryKeyword: 'pad thai seattle',
    secondaryKeywords: [
      'pad thai near me',
      'best pad thai seattle',
      'chicken pad thai seattle',
      'vegetarian pad thai seattle',
      'pad thai near uw',
    ],
    related: ['pad-see-ew', 'drunken-noodles', 'khao-soi'],
  },

  {
    slug: 'khao-soi',
    name: 'Khao Soi',
    menuName: 'Khao Soi',
    thaiScript: 'ข้าวซอย',
    romanization: 'khao soi',
    menuSection: 'Noodle',
    h1: 'Khao Soi in North Seattle',
    basePrice: 15.95,
    menuDescription:
      'Egg noodle in special curry sauce topped with crispy noodle, shallot, lime and chili oil.',
    proteins: NOODLE_PROTEINS,
    intro: [
      'Khao soi is the curry noodle soup of northern Thailand, most closely associated with Chiang Mai. What makes it distinct from every other Thai noodle dish is the double treatment of the noodle: soft egg noodles sit in the curry broth, and a tangle of crisp fried egg noodles goes on top.',
      'Thaprachan serves it from the Noodle section — egg noodle in a special curry sauce, topped with crispy noodle, shallot, lime and chili oil. We are on NE 65th St in north Seattle, near the University of Washington.',
    ],
    ingredients: [
      'Egg noodle',
      'Special curry sauce',
      'Crispy noodle',
      'Shallot',
      'Lime',
      'Chili oil',
    ],
    faqs: [
      {
        question: 'What is khao soi?',
        answer:
          'Khao soi is a northern Thai curry noodle soup, associated with Chiang Mai. Egg noodles are served in a creamy curry broth and topped with crisp fried egg noodles, usually with lime and raw onion alongside to cut the richness.',
      },
      {
        question: 'How much is khao soi at Thaprachan?',
        answer: `Khao Soi is $15.95. ${proteinSentence}`,
      },
      {
        question: 'What is it served with?',
        answer:
          'It is topped with crispy noodle, shallot, lime and chili oil.',
      },
      {
        question: 'Where can I find khao soi in north Seattle?',
        answer:
          'Thaprachan Thai Cuisine serves khao soi at 805 NE 65th St, Seattle, WA 98115, in north Seattle, about a 10-minute drive from the University of Washington.',
      },
    ],
    bestSection: {
      heading: 'The best khao soi in Seattle',
      body:
        'Khao soi is a northern Thai dish, so it turns up on far fewer Seattle menus than pad thai does — which makes it worth crossing town for. We serve the best khao soi in north Seattle: egg noodle in a special curry sauce, topped with crispy noodle, shallot, lime and chili oil, on NE 65th St.',
    },
    title: 'Best Khao Soi in North Seattle | Thaprachan Thai',
    metaDescription:
      'Khao soi — northern Thai curry noodle soup with crispy noodle, shallot and lime, $15.95 — at Thaprachan Thai Cuisine in north Seattle. Order online.',
    primaryKeyword: 'khao soi seattle',
    secondaryKeywords: [
      'khao soi north seattle',
      'best khao soi seattle',
      'khao soi near me',
      'khao soi restaurant seattle',
      'thai curry noodle soup seattle',
    ],
    related: ['pad-thai', 'drunken-noodles', 'mango-sticky-rice'],
  },

  {
    slug: 'pad-see-ew',
    name: 'Pad See Ew',
    menuName: 'Pad Se-Ew',
    thaiScript: 'ผัดซีอิ๊ว',
    romanization: 'phat si io',
    menuSection: 'Noodle',
    h1: 'Pad See Ew in Seattle',
    basePrice: 14.45,
    menuDescription: 'Stir-fried wide noodles with egg, broccoli in sweet soy sauce.',
    proteins: NOODLE_PROTEINS,
    intro: [
      'Pad see ew is the wide-noodle counterpart to pad thai: flat rice noodles seared hard in a wok with sweet soy sauce, egg and greens. Where pad thai is sweet, sour and nutty, pad see ew is savory, smoky and mild.',
      'On the Thaprachan menu it is printed as Pad Se-Ew — the same dish, spelled the way our kitchen has always spelled it. Stir-fried wide noodles with egg and broccoli in sweet soy sauce, with your choice of protein.',
    ],
    ingredients: [
      'Wide rice noodles',
      'Egg',
      'Broccoli',
      'Sweet soy sauce',
    ],
    faqs: [
      {
        question: 'Is "Pad Se-Ew" on your menu the same as pad see ew?',
        answer:
          'Yes. Pad Se-Ew is how the dish is printed on our menu, and it is the same dish usually written as pad see ew or phat si io — stir-fried wide noodles with egg and broccoli in sweet soy sauce.',
      },
      {
        question: 'What is the difference between pad see ew and pad thai?',
        answer:
          'Both are stir-fried noodle dishes, but they use different noodles and different sauces. Our Pad Se-Ew is wide noodles with egg and broccoli in sweet soy sauce. Our Pad Thai is thin rice noodles with egg, bean sprouts and scallion in a homemade sauce, topped with crushed peanuts.',
      },
      {
        question: 'How much is pad see ew at Thaprachan?',
        answer: `Pad Se-Ew is $14.45. ${proteinSentence}`,
      },
      {
        question: 'Can I order it without meat?',
        answer:
          'Yes. Tofu and Vegetable are available as the base protein at no extra cost. The dish as written includes egg.',
      },
    ],
    bestSection: {
      heading: 'The best pad see ew in Seattle',
      body:
        'The best pad see ew in Seattle comes down to the wok — wide noodles need real heat to pick up the char that makes the dish. Ours is stir-fried with egg and broccoli in sweet soy sauce, and it is printed on our menu as Pad Se-Ew if you are scanning for it.',
    },
    title: 'Best Pad See Ew in Seattle | Thaprachan Thai',
    metaDescription:
      'Pad see ew (Pad Se-Ew) at Thaprachan Thai Cuisine, Seattle — wide noodles, egg and broccoli in sweet soy sauce, $14.45. Order online or call.',
    primaryKeyword: 'pad see ew seattle',
    secondaryKeywords: [
      'pad see ew near me',
      'pad se-ew',
      'chicken pad see ew seattle',
      'pad see ew vs pad thai',
      'wide noodle thai seattle',
    ],
    related: ['drunken-noodles', 'pad-thai', 'khao-soi'],
  },

  {
    slug: 'drunken-noodles',
    name: 'Drunken Noodles',
    menuName: 'Kee Mao',
    thaiScript: 'ผัดขี้เมา',
    romanization: 'phat khi mao',
    menuSection: 'Noodle',
    h1: 'Drunken Noodles (Kee Mao) in Seattle',
    basePrice: 14.45,
    menuDescription:
      'Stir-fried wide noodles with egg, basil, bell peppers, yellow onions, and tomatoes.',
    proteins: NOODLE_PROTEINS,
    intro: [
      'Drunken noodles — pad kee mao in Thai — is the basil-and-vegetable wide noodle stir-fry that sits alongside pad see ew on almost every Thai menu in America. The name has nothing to do with alcohol in the dish.',
      'Our menu lists it under its Thai name, Kee Mao. It is stir-fried wide noodles with egg, basil, bell peppers, yellow onions and tomatoes, with your choice of protein.',
    ],
    ingredients: [
      'Wide rice noodles',
      'Egg',
      'Basil',
      'Bell peppers',
      'Yellow onions',
      'Tomatoes',
    ],
    faqs: [
      {
        question: 'Is Kee Mao the same as drunken noodles?',
        answer:
          'Yes. The dish printed as Kee Mao on our menu is the dish commonly called drunken noodles in English, and pad kee mao in Thai.',
      },
      {
        question: 'What is the difference between drunken noodles and pad see ew?',
        answer:
          'Both use wide noodles and egg. Our Kee Mao is stir-fried with basil, bell peppers, yellow onions and tomatoes. Our Pad Se-Ew is simpler — broccoli in sweet soy sauce.',
      },
      {
        question: 'How much are drunken noodles at Thaprachan?',
        answer: `Kee Mao is $14.45. ${proteinSentence}`,
      },
      {
        question: 'Where can I get drunken noodles in Seattle?',
        answer:
          'Thaprachan Thai Cuisine is at 805 NE 65th St, Seattle, WA 98115, in north Seattle near the University of Washington. Order online or call (206) 523-3938.',
      },
    ],
    bestSection: {
      heading: 'The best drunken noodles in Seattle',
      body:
        'The best drunken noodles in Seattle should have more going on than a plate of pad see ew — the same wide noodles, but stir-fried with basil, bell peppers, yellow onions and tomatoes. Look for it as Kee Mao on our menu.',
    },
    title: 'Best Drunken Noodles in Seattle | Thaprachan Thai',
    metaDescription:
      'Drunken noodles — Kee Mao — at Thaprachan Thai Cuisine, Seattle. Wide noodles with egg, basil, peppers, onion and tomato, $14.45. Order online.',
    primaryKeyword: 'drunken noodles seattle',
    secondaryKeywords: [
      'drunken noodles near me',
      'pad kee mao seattle',
      'kee mao',
      'drunken noodles vs pad see ew',
      'thai basil noodles seattle',
    ],
    related: ['pad-see-ew', 'pad-thai', 'khao-soi'],
  },

  {
    slug: 'mango-sticky-rice',
    name: 'Mango Sticky Rice',
    menuName: 'Mango Sticky Rice',
    thaiScript: 'ข้าวเหนียวมะม่วง',
    romanization: 'khao niao mamuang',
    menuSection: 'Desserts',
    h1: 'Mango Sticky Rice in Seattle',
    basePrice: 10.95,
    menuDescription:
      'Seasonal mango with sweet sticky rice, topped with sesame seed and coconut milk.',
    intro: [
      'Mango sticky rice — khao niao mamuang — is the best known dessert in Thai cooking: ripe mango served with glutinous rice steeped in sweetened coconut milk. It is eaten warm or at room temperature, never frozen, and the contrast between the cool fruit and the rich rice is the whole point of the dish.',
      'Thaprachan serves it from the dessert menu: seasonal mango with sweet sticky rice, topped with sesame seed and coconut milk.',
    ],
    ingredients: [
      'Seasonal mango',
      'Sweet sticky rice',
      'Sesame seed',
      'Coconut milk',
    ],
    faqs: [
      {
        question: 'What is mango sticky rice?',
        answer:
          'Mango sticky rice, khao niao mamuang, is a Thai dessert of ripe mango served with glutinous rice and sweetened coconut milk. Ours is topped with sesame seed and coconut milk.',
      },
      {
        question: 'How much is mango sticky rice at Thaprachan?',
        answer: 'Mango Sticky Rice is $10.95 on our dessert menu.',
      },
      {
        question: 'Is mango sticky rice available all year?',
        answer:
          'The dish is made with seasonal mango, so availability depends on the fruit. Call (206) 523-3938 to check before you come in for it.',
      },
      {
        question: 'What other Thai desserts do you serve?',
        answer:
          'Our dessert menu also has Black Sticky Rice, Roti with Banana, Thai Tea Cheesecake, and mango ice cream.',
      },
    ],
    bestSection: {
      heading: 'The best mango sticky rice in Seattle',
      body:
        'The best mango sticky rice in Seattle is the one made fresh rather than pulled from a fridge — the rice warm, the mango cool. Ours is seasonal mango with sweet sticky rice, topped with sesame seed and coconut milk, on the dessert menu alongside black sticky rice and roti with banana.',
    },
    title: 'Best Mango Sticky Rice in Seattle | Thaprachan',
    metaDescription:
      'Mango sticky rice at Thaprachan Thai Cuisine in Seattle — seasonal mango, sweet sticky rice, sesame seed and coconut milk, $10.95. Order online.',
    primaryKeyword: 'mango sticky rice seattle',
    secondaryKeywords: [
      'mango sticky rice near me',
      'best mango sticky rice seattle',
      'thai dessert seattle',
      'khao niao mamuang seattle',
    ],
    related: ['khao-soi', 'pad-thai', 'pad-see-ew'],
  },
];

export const getDish = (slug?: string) => dishes.find((d) => d.slug === slug);

export const formatPrice = (n: number) => `$${n.toFixed(2)}`;
